/**
 * Passkeys in the browser (#21): `navigator.credentials` in both directions.
 *
 * **No wording and no rule lives here** -- only the repacking between the
 * server's JSON (base64url) and the `ArrayBuffer`s that WebAuthn wants.
 * Whether a passkey is valid is decided by the server (`Passkeys.php`); what
 * is shown is said by the components via `useTexte()`.
 *
 * **Repacked by hand and not via `parseCreationOptionsFromJSON()`**: that
 * exists only from Safari 17.4 and Chrome 118 on, and an iPad that can no
 * longer update to the latest version should still be able to create the
 * passkey. Twenty lines against an age limit.
 */

export interface RegistrationOptions {
  rp: { id: string; name: string }
  user: { id: string; name: string; displayName: string }
  challenge: string
  pubKeyCredParams: Array<{ type: 'public-key'; alg: number }>
  authenticatorSelection: AuthenticatorSelectionCriteria
  attestation: AttestationConveyancePreference
  timeout: number
  excludeCredentials: Array<{ type: 'public-key'; id: string }>
}

export interface LoginOptions {
  challenge: string
  rpId: string
  userVerification: UserVerificationRequirement
  timeout: number
  allowCredentials: Array<{ type: 'public-key'; id: string }>
}

/** What goes to `POST /api/auth/passkeys`. */
export interface Registration {
  id: string
  rawId: string
  type: string
  response: { clientDataJSON: string; attestationObject: string; transports: string[] }
  name: string
}

/** What goes to `POST /api/auth/passkeys/login`. */
export interface Login {
  id: string
  rawId: string
  type: string
  response: {
    clientDataJSON: string
    authenticatorData: string
    signature: string
    userHandle: string | null
  }
}

/** Can this browser do passkeys at all -- and is the page secure enough for it? */
export function passkeyAvailable(): boolean {
  return (
    typeof window !== 'undefined'
    && 'PublicKeyCredential' in window
    && window.isSecureContext
    && typeof navigator.credentials?.create === 'function'
  )
}

/**
 * Does the browser offer the passkey by itself in the address field (autofill)?
 *
 * That is the way iOS goes: no button, but the suggestion above the
 * keyboard. The button stays anyway -- for browsers that do not do this, and
 * for whoever dismissed the suggestion.
 */
export async function autofillAvailable(): Promise<boolean> {
  if (!passkeyAvailable()) return false
  const k = PublicKeyCredential as unknown as {
    isConditionalMediationAvailable?: () => Promise<boolean>
  }
  if (typeof k.isConditionalMediationAvailable !== 'function') return false
  try {
    return await k.isConditionalMediationAvailable()
  } catch {
    return false
  }
}

/** Did the person cancel (or we ourselves, via `AbortController`)? */
export function isAbort(error: unknown): boolean {
  return error instanceof DOMException
    && (error.name === 'NotAllowedError' || error.name === 'AbortError')
}


export async function createPasskey(
  options: RegistrationOptions,
  name: string,
): Promise<Registration> {
  const publicKey: PublicKeyCredentialCreationOptions = {
    rp: options.rp,
    user: {
      id: fromBase64url(options.user.id),
      name: options.user.name,
      displayName: options.user.displayName,
    },
    challenge: fromBase64url(options.challenge),
    pubKeyCredParams: options.pubKeyCredParams,
    authenticatorSelection: options.authenticatorSelection,
    attestation: options.attestation,
    timeout: options.timeout,
    excludeCredentials: options.excludeCredentials.map((c) => ({
      type: c.type,
      id: fromBase64url(c.id),
    })),
  }
  const credential = (await navigator.credentials.create({ publicKey })) as PublicKeyCredential | null
  if (credential === null) throw new DOMException('no passkey', 'NotAllowedError')

  const response = credential.response as AuthenticatorAttestationResponse & {
    getTransports?: () => string[]
  }
  return {
    id: credential.id,
    rawId: toBase64url(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: toBase64url(response.clientDataJSON),
      attestationObject: toBase64url(response.attestationObject),
      transports: typeof response.getTransports === 'function' ? response.getTransports() : [],
    },
    name,
  }
}

export async function loginWithPasskey(
  options: LoginOptions,
  signal?: AbortSignal,
  mediation?: CredentialMediationRequirement,
): Promise<Login> {
  const publicKey: PublicKeyCredentialRequestOptions = {
    challenge: fromBase64url(options.challenge),
    rpId: options.rpId,
    userVerification: options.userVerification,
    timeout: options.timeout,
    allowCredentials: options.allowCredentials.map((c) => ({
      type: c.type,
      id: fromBase64url(c.id),
    })),
  }
  const credential = (await navigator.credentials.get({
    publicKey,
    signal,
    mediation,
  })) as PublicKeyCredential | null
  if (credential === null) throw new DOMException('no passkey', 'NotAllowedError')

  const response = credential.response as AuthenticatorAssertionResponse
  return {
    id: credential.id,
    rawId: toBase64url(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: toBase64url(response.clientDataJSON),
      authenticatorData: toBase64url(response.authenticatorData),
      signature: toBase64url(response.signature),
      userHandle: response.userHandle ? toBase64url(response.userHandle) : null,
    },
  }
}

function fromBase64url(text: string): ArrayBuffer {
  const b64 = text.replace(/-/g, '+').replace(/_/g, '/')
  const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4)
  const bin = atob(padded)
  const buffer = new ArrayBuffer(bin.length)
  const bytes = new Uint8Array(buffer)
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
  return buffer
}

function toBase64url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let bin = ''
  for (const b of bytes) bin += String.fromCharCode(b)
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}
